// /src/components/donate/KofiDonationLink.jsx

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../authContext';
import { useSecurity } from '../../SecurityContext';
import { createKofiDonationIntent } from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelApi';

export default function KofiDonationLink() {
  const { isLoggedIn, email } = useAuth();
  const { isViewAsActive, viewAsUserId, viewAsLoading } = useSecurity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const loadIntent = useCallback(async () => {
    if (!isLoggedIn) return;
    if (isViewAsActive && viewAsLoading) return;
    setLoading(true);
    setError('');
    try {
      const data = await createKofiDonationIntent();
      if (!data?.ok) {
        throw new Error(data?.error || 'Could not prepare donation link.');
      }
      setCode(data?.data?.code || '');
      setAccountEmail(data?.data?.email || (isViewAsActive ? '' : email) || '');
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not prepare donation link.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [email, isLoggedIn, isViewAsActive, viewAsLoading, viewAsUserId]);

  useEffect(() => {
    loadIntent();
  }, [loadIntent]);

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="kofi-donation-link kofi-donation-link--guest">
        <p className="kofi-donation-link__title">Link your donation</p>
        <p className="kofi-donation-link__text">
          Log in to receive a personal code and link your donation to your Pallas account.
        </p>
      </div>
    );
  }

  return (
    <div
      className="kofi-donation-link"
      data-resource="public.donation-panel"
      data-action="read"
      data-route="/api/kofi/donation-intent"
      data-description="Ko-fi donation linking"
    >
      <p className="kofi-donation-link__title">
        {isViewAsActive ? 'Link their donation' : 'Link your donation'}
      </p>
      {loading || (isViewAsActive && viewAsLoading) ? (
        <p className="kofi-donation-link__text">Preparing donation code…</p>
      ) : null}
      {error ? (
        <p className="kofi-donation-link__msg kofi-donation-link__msg--error" role="alert">
          {error}
        </p>
      ) : null}
      {!loading && !(isViewAsActive && viewAsLoading) && !error ? (
        <>
          {accountEmail ? (
            <p className="kofi-donation-link__text">
              Use email <strong>{accountEmail}</strong> on Ko-fi, or paste this code in
              {' '}
              <strong>Your message</strong>
              :
            </p>
          ) : (
            <p className="kofi-donation-link__text">
              Paste this code in <strong>Your message</strong> on Ko-fi:
            </p>
          )}
          <div className="kofi-donation-link__code-row">
            <code className="kofi-donation-link__code">{code || '—'}</code>
            <button
              type="button"
              className="kofi-donation-link__copy"
              onClick={handleCopy}
              disabled={!code}
              data-resource="public.donation-panel"
              data-action="read"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
